import apiService from './api';

export interface Order {
  id: string;
  totalAmount: number;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  createdAt: Date;
  items: OrderItem[];
  orderNumber?: string;
  shippingAddress?: string;
  shippingFee?: number;
  paymentMethod?: string;
  firstName?: string;
  lastName?: string;
  shippingEmail?: string;
  shippingPhone?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  orderNote?: string;
  user?: {
    id: number;
    name: string;
    email: string;
    phoneNumber?: string;
  };
}

export interface OrderItem {
  id: number;
  productId: number;
  productName: string;
  quantity: number;
  price: number;
  discountPrice?: number;
  imageUrl?: string;
  product?: {
    id: number;
    name: string;
    price: number;
    discountPrice?: number;
    imageUrl?: string;
  };
}

export interface OrderDetail extends Order {
  items: OrderItem[];
  shippingAddress: string;
  billingAddress: string;
  paymentMethod: string;
  trackingNumber?: string;
  estimatedDelivery?: Date;
}

export interface PlaceOrderData {
  items: Array<{
    productId: number;
    quantity: number;
  }>;
  address: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  orderNote?: string;
  country: string;
  paymentMethod: string;
  shippingType: string;
  shippingFee?: number;
}

class OrderService {
  async placeOrder(placeOrderData: PlaceOrderData, userId?: number): Promise<Order> {
    try {
      // Get userId from localStorage if not provided
      let currentUserId = userId;
      if (!currentUserId) {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          currentUserId = user.id;
        }
      }

      if (!currentUserId) {
        throw new Error('User ID is required to place an order');
      }

      // Ensure userId is a number
      const userIdNumber = typeof currentUserId === 'string' ? Number.parseInt(currentUserId, 10) : currentUserId;
      if (Number.isNaN(userIdNumber) || userIdNumber <= 0) {
        throw new Error('Invalid user ID. Please login again.');
      }

      // Call the backend API to place the order
      const response = await apiService.post<Order>(
        `/orders/placeOrder?userId=${userIdNumber}`,
        placeOrderData
      );

      return response;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to place order');
    }
  }

  async getUserOrders(userId?: number): Promise<Order[]> {
    try {
      // Get userId from localStorage if not provided
      let currentUserId = userId;
      if (!currentUserId) {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          currentUserId = user.id;
        }
      }

      // Use the correct backend endpoint
      const endpoint = currentUserId
        ? `/orders/getUserOrders?userId=${currentUserId}`
        : '/orders/getUserOrders';

      const response = await apiService.get<Order[]>(endpoint);
      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      // If it's a 404, return empty array (endpoint might not be implemented yet)
      if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
        return [];
      }
      throw new Error(errorMessage || 'Failed to fetch user orders');
    }
  }

  async getOrderById(orderId: string): Promise<OrderDetail> {
    try {
      const response = await apiService.get<OrderDetail>(`/orders/getOrderById/${orderId}`);
      return response;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch order details');
    }
  }

  async cancelOrder(orderId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiService.post<{ success: boolean; message: string }>(`/orders/${orderId}/cancel`);
      return response;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to cancel order');
    }
  }

  async getOrderHistory(): Promise<Order[]> {
    try {
      const response = await apiService.get<Order[]>('/orders/history');
      return response;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch order history');
    }
  }

  async getOrdersByStatus(status: string): Promise<Order[]> {
    try {
      const response = await apiService.get<Order[]>(`/orders/status/${status}`);
      return response;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch orders by status');
    }
  }

  async getAllOrders(): Promise<Order[]> {
    try {
      const response = await apiService.get<Order[]>('/orders/getAllOrders');
      return response;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch all orders');
    }
  }

  async updateOrderStatus(orderId: string, status: Order['status']): Promise<Order> {
    try {
      const response = await apiService.put<Order>(
        `/orders/updateOrderStatus/${orderId}`,
        { status }
      );
      return response;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to update order status');
    }
  }

  async resendConfirmation(orderId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiService.post<{ success: boolean; message: string }>(
        `/orders/${orderId}/resend-confirmation`
      );
      return response;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to resend confirmation email');
    }
  }
}

export const orderService = new OrderService();
export default orderService;
